import { useContext, useMemo } from 'react'
import moment from 'moment'

import GeoContext from 'contexts/geoContext'
import type { OfferDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import { getStopHotelItems, isBookedHotelItem, type TripHotelItem } from 'tripPlanner/utils/chatItinerary'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { displayPricingFromDTO } from 'tripPlanner/utils/accommodationPricingDisplay'
import useOfferDetails from './useOfferDetails'

export interface BookedHotelSummary {
  imageId?: string
  name: string
  nights?: number
  offerId?: string
  offerType?: string
}

export interface HotelTilePrice {
  currency?: string
  price: number
}

interface UseTripPlanHotelTileDataProps {
  currentStop?: TripItineraryData['stops'][number]
}

function getHotelItemImageId(item: TripHotelItem): string | undefined {
  const savedData = item.savedItemData
  if (isHotelSavedData(savedData) && savedData.imageIds?.length) {
    return savedData.imageIds[0]
  }

  if ('itemPlaceDetail' in item) {
    return item.itemPlaceDetail?.imageIds?.[0]
  }

  return undefined
}

function getHotelItemNights(
  item: TripHotelItem,
): number | undefined {
  const savedData = item.savedItemData
  const itemStartDate = 'startDate' in item ? item.startDate : undefined
  const itemEndDate = 'endDate' in item ? item.endDate : undefined
  const bookedRoomDates = isHotelSavedData(savedData) ?
      savedData.bookedRooms?.find((room): room is typeof room & { checkIn: string; checkOut: string } => !!('checkIn' in room && room.checkIn && 'checkOut' in room && room.checkOut)) :
    undefined
  const checkIn = isHotelSavedData(savedData) ?
    savedData.checkIn || bookedRoomDates?.checkIn || itemStartDate :
    itemStartDate
  const checkOut = isHotelSavedData(savedData) ?
    savedData.checkOut || bookedRoomDates?.checkOut || itemEndDate :
    itemEndDate

  if (!checkIn || !checkOut) {
    return undefined
  }

  const nights = moment(checkOut).diff(moment(checkIn), 'days')
  if (nights > 0) return nights

  return undefined
}

function getHotelDetailBookedRoomNights(offerDetail: OfferDetailResult | undefined): number | undefined {
  const bookedRoomDates = offerDetail?.bookedRooms?.find(room => !!room.checkIn && !!room.checkOut)
  if (!bookedRoomDates) return undefined

  const nights = moment(bookedRoomDates.checkOut).diff(moment(bookedRoomDates.checkIn), 'days')
  return nights > 0 ? nights : undefined
}

export default function useTripPlanHotelTileData({
  currentStop,
}: UseTripPlanHotelTileDataProps) {
  const geo = useContext(GeoContext)

  const stopHotelItems = useMemo(() => {
    return getStopHotelItems(currentStop)
  }, [currentStop])

  const savedHotelItems = useMemo(() => {
    return stopHotelItems.filter((item) => isHotelSavedData(item.savedItemData))
  }, [stopHotelItems])

  const bookedHotelItem = useMemo(() => {
    return stopHotelItems.find(isBookedHotelItem)
  }, [stopHotelItems])

  const bookedHotelSavedData = bookedHotelItem?.savedItemData
  const bookedHotelOfferId = isHotelSavedData(bookedHotelSavedData) ? bookedHotelSavedData.offerId : undefined
  const bookedHotelOfferType = isHotelSavedData(bookedHotelSavedData) ? bookedHotelSavedData.offerType : undefined
  const bookedHotelOrderId = bookedHotelItem && 'confirmationCode' in bookedHotelItem && typeof bookedHotelItem.confirmationCode === 'string' ?
    bookedHotelItem.confirmationCode :
    undefined
  const bookedHotelItemNights = useMemo(() => {
    return bookedHotelItem ? getHotelItemNights(bookedHotelItem) : undefined
  }, [bookedHotelItem])

  const { offerDetail: bookedHotelOfferDetail } = useOfferDetails({
    offerId: bookedHotelOfferId,
    region: geo.currentRegionCode,
    offerType: bookedHotelOfferType,
    orderId: bookedHotelOrderId,
    enabled: bookedHotelItemNights === undefined && !!bookedHotelOfferId && !!bookedHotelOrderId,
  })

  const bookedHotelSummary = useMemo<BookedHotelSummary | undefined>(() => {
    if (!bookedHotelItem) return undefined

    return {
      name: bookedHotelItem.name,
      nights: bookedHotelItemNights ?? getHotelDetailBookedRoomNights(bookedHotelOfferDetail),
      imageId: getHotelItemImageId(bookedHotelItem),
      offerId: bookedHotelOfferId,
      offerType: bookedHotelOfferType,
    }
  }, [bookedHotelItem, bookedHotelItemNights, bookedHotelOfferDetail, bookedHotelOfferId, bookedHotelOfferType])

  const savedHotelPrice = useMemo(() => {
    const firstPricedSavedHotel = savedHotelItems.find((item) => {
      const savedData = item.savedItemData
      return isHotelSavedData(savedData) && (savedData.pricing?.amount ?? 0) > 0
    })
    const savedData = firstPricedSavedHotel?.savedItemData
    if (!firstPricedSavedHotel || !isHotelSavedData(savedData) || !savedData.pricing) return undefined

    const displayPricing = displayPricingFromDTO(savedData.pricing)
    if (displayPricing.price === undefined) return undefined

    const nights = savedData.pricing.durationNights ?? getHotelItemNights(firstPricedSavedHotel) ?? 1
    const nightlyPrice = displayPricing.price / nights
    if (!Number.isFinite(nightlyPrice) || nightlyPrice <= 0) return undefined

    return {
      price: nightlyPrice,
      currency: savedData.pricing.currencyCode,
    }
  }, [savedHotelItems])

  const savedHotelCount = savedHotelItems.length
  const hasAccommodation = stopHotelItems.length > 0

  return {
    bookedHotelSummary,
    hasAccommodation,
    hotelTilePrice: savedHotelPrice,
    savedHotelCount,
  }
}
